/* eslint-disable perfectionist/sort-imports */
import 'src/utils/highlight';

import ReactQuill from 'react-quill';

import { alpha } from '@mui/material/styles';

import { useRef, useEffect, forwardRef } from 'react';
import { StyledEditor } from './styles';
import Toolbar, { formats } from './toolbar';
import { insertSanitizedPaste } from './sanitize-clipboard';
import { EditorProps } from './types';

// ----------------------------------------------------------------------

const Editor = forwardRef<ReactQuill, EditorProps>(({
  id = 'minimal-quill',
  error,
  simple = false,
  noHeader = false,
  helperText,
  disabled,
  sx,
  formats: formatsProp,
  ...other
}, ref) => {
  
  const quillRef = useRef<ReactQuill>(null);

  const modules = {
    toolbar: {
      container: `#${id}`,
    },
    history: {
      delay: 500,
      maxStack: 100,
      userOnly: false,
    },
    syntax: true,
    clipboard: {
      matchVisual: false,
    },
  };

  useEffect(() => {
    if (!quillRef.current) {
      return;
    }

    const quill = quillRef.current.getEditor();

    const handlePaste = (event: ClipboardEvent) => insertSanitizedPaste(quill, event);

    quill.root.addEventListener('paste', handlePaste);

    // eslint-disable-next-line consistent-return
    return () => {
      quill.root.removeEventListener('paste', handlePaste);
    };
  }, []);

  return (
    <>
      <StyledEditor
        sx={{
          ...(error && {
            border: (theme) => `solid 1px ${theme.palette.error.main}`,
            '& .ql-editor': {
              bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
            },
          }),
          ...sx,
        }}
      >
        <Toolbar id={id} simple noHeader={noHeader} />

        <ReactQuill
          ref={(instance) => {
            // Update internal ref
            (quillRef as React.MutableRefObject<ReactQuill | null>).current = instance;
            // Forward ref to parent
            if (ref) {
              if (typeof ref === 'function') {
                ref(instance);
              } else {
                (ref as React.MutableRefObject<ReactQuill | null>).current = instance;
              }
            }
          }}
          modules={modules}
          formats={formatsProp ?? formats}
          readOnly={disabled}
          theme="snow"
          placeholder="Write something awesome..."
          {...other}
        />
      </StyledEditor>

      {helperText && helperText}
    </>
  );
});

Editor.displayName = 'Editor';

export default Editor;
